import { cannedSettings } from "./canned-settings";
const communityTypeQuestionKey = "Question_CommunityTypes";
const otherStylesKey = "Option_OtherStyles";
const statesExcludingDefaultMedicaidQuestion = ["ca", "az"];
export const statesUsingLicenseRecords = ["az", "wa", "or", "ca"];
function buildFloorplanQuestion(communityTypeLabel, communityTypeOptionKey, floorplans, floorplanQuestionKey, getNextCommunityTypeOrder) {
    const prompts = [];
    let orderOption = 1000;
    for (const roomTypeLabel of floorplans) {
        prompts.push({
            client: roomTypeLabel,
            community: roomTypeLabel,
            key: `Option_${roomTypeLabel.replace(/\s/g, "")}`,
            order: orderOption,
        });
        orderOption += 1000;
    }
    prompts.push({
        client: "",
        community: "Other Styles",
        key: otherStylesKey,
        order: orderOption,
    });
    let floorPlanLabel = "Floor Plans";
    if (floorplans.length <= 2) {
        floorPlanLabel = "Room Types";
    }
    const title = `${communityTypeLabel} -- ${floorPlanLabel}`;
    const rule = {
        contains: communityTypeOptionKey,
        operator: "naryvalue",
        question: communityTypeQuestionKey,
    };
    return {
        category: "type",
        clientInteraction: "multiple",
        clientTitle: title,
        communityInteraction: "multiple",
        communityTitle: title,
        internal_key: title.replace(/\s/g, "") + "_key",
        key: floorplanQuestionKey,
        matchingCriteria: "clientsubset",
        order: getNextCommunityTypeOrder(),
        prompts,
        questionType: "nary",
        supportAnyAndAllSearch: "true",
        clientRules: rule,
        communityRules: rule,
    };
}
function generalPriceRangeForType(communityTypeLabel, communityTypeOptionKey, getNextFinancialOrder) {
    function getCommunityRules() {
        return {
            contains: communityTypeOptionKey,
            operator: "naryvalue",
            question: communityTypeQuestionKey,
        };
    }
    const communityTypePricing = {
        category: "finance",
        clientInteraction: "none",
        communityInteraction: "currency",
        questionType: "range",
        communityTitle: `${communityTypeLabel} -- Price`,
        key: `Question_${communityTypeLabel}_PriceRange`,
        order: getNextFinancialOrder(),
        communityRules: getCommunityRules(),
    };
    return communityTypePricing;
}
function buildSqFtQuestion(communityTypeLabel, roomTypeLabel, floorplanQuestionKey, getNextCommunityTypeOrder) {
    const squareFootage = {
        category: "type",
        clientInteraction: "none",
        communityInteraction: "number",
        questionType: "range",
        communityTitle: `${communityTypeLabel} -- ${roomTypeLabel} Square Footage`,
        key: `Question_${communityTypeLabel.replace(/\s/g, "")}_${roomTypeLabel.replace(/\s/g, "")}_SquareFootage`,
        order: getNextCommunityTypeOrder(),
    };
    const rule = {
        contains: `Option_${roomTypeLabel.replace(/\s/g, "")}`,
        operator: "naryvalue",
        question: floorplanQuestionKey,
    };
    squareFootage["communityRules"] = rule;
    return squareFootage;
}
function buildQuestionsForCommunityType(communityTypeLabel, communityTypeOptionKey, floorplans, prefs, getNextCommunityTypeOrder, getNextFinancialOrder) {
    if (prefs.floorplanPricing && floorplans.length > 0) {
        const floorplanQuestionKey = "Question_" + communityTypeOptionKey.replace("Option_", "") + "_RoomTypes";
        const result = [
            buildFloorplanQuestion(communityTypeLabel, communityTypeOptionKey, floorplans, floorplanQuestionKey, getNextCommunityTypeOrder),
        ];
        for (const roomTypeLabel of floorplans) {
            if (prefs.squareFootage) {
                result.push(buildSqFtQuestion(communityTypeLabel, roomTypeLabel, floorplanQuestionKey, getNextCommunityTypeOrder));
            }
            const floorPlanPricing = {
                category: "finance",
                clientInteraction: "none",
                communityInteraction: "currency",
                questionType: "range",
                communityTitle: communityTypeLabel + " -- " + roomTypeLabel + " Price",
                key: "Question_" +
                    communityTypeLabel.replace(/\s/g, "") +
                    "_" +
                    roomTypeLabel.replace(/\s/g, "") +
                    "_PriceRange",
                order: getNextFinancialOrder(),
                communityRules: {
                    contains: "Option_" + roomTypeLabel.replace(/\s/g, ""),
                    operator: "naryvalue",
                    question: floorplanQuestionKey,
                },
            };
            result.push(floorPlanPricing);
        }
        const floorPlanPricing = {
            category: "finance",
            clientInteraction: "none",
            communityInteraction: "multiline",
            questionType: "text",
            communityTitle: communityTypeLabel + " -- " + "Other Pricing",
            key: "Question_" +
                communityTypeLabel.replace(/\s/g, "") +
                "_" +
                "OtherPricing",
            order: getNextFinancialOrder(),
            communityRules: {
                contains: otherStylesKey,
                operator: "naryvalue",
                question: floorplanQuestionKey,
            },
        };
        result.push(floorPlanPricing);
        return result;
    }
    else {
        return [
            generalPriceRangeForType(communityTypeLabel, communityTypeOptionKey, getNextFinancialOrder),
        ];
    }
}
function mergeJson(states) {
    const settingsJson = [];
    let alreadyAddedMedicaid = false;
    let alreadyAddedLicenseQuestions = false;
    for (const state of states) {
        if (cannedSettings[state]) {
            settingsJson.push(...cannedSettings[state]);
        }
        if (!statesExcludingDefaultMedicaidQuestion.includes(state) &&
            !alreadyAddedMedicaid) {
            alreadyAddedMedicaid = true;
            settingsJson.push(...cannedSettings.medicaid);
        }
        if (!statesUsingLicenseRecords.includes(state) && !alreadyAddedLicenseQuestions) {
            alreadyAddedLicenseQuestions = true;
            settingsJson.push(...cannedSettings.license);
        }
    }
    settingsJson.push(...cannedSettings.core);
    return settingsJson;
}
export const generateCommunityTypeQuestions = (entries, prefs) => {
    let communityTypeOrder = 5000 - 200;
    let financialOrder = 1000 - 200;
    const getNextCommunityTypeOrder = () => {
        communityTypeOrder += 200;
        return communityTypeOrder;
    };
    const getNextFinancialOrder = () => {
        financialOrder += 200;
        return financialOrder;
    };
    const settingsJson = [];
    const questionTitle = "Community Type(s)";
    const communityTypePrompts = [];
    const communityQuestion = {
        category: "type",
        clientInteraction: "multiple",
        clientTitle: questionTitle,
        communityInteraction: "multiple",
        communityTitle: questionTitle,
        internal_key: "ProviderTypes",
        key: communityTypeQuestionKey,
        matchingCriteria: "clientsubset",
        order: 100,
        prompts: communityTypePrompts,
        questionType: "nary",
        supportAnyAndAllSearch: "true",
    };
    for (let index = 0; index < entries.length; index++) {
        const row = entries[index];
        const communityType = row.communityType;
        const optionKey = "Option_" + row.communityType.replace(/\s/g, "");
        const typeOption = {
            client: communityType,
            community: communityType,
            key: optionKey,
            order: row.order,
        };
        communityTypePrompts.push(typeOption);
        let floorplans = [];
        if (row.pricing === "perFloorplan") {
            floorplans = row.floorplans ?? [];
        }
        if (row.pricing !== "none") {
            settingsJson.push(...buildQuestionsForCommunityType(communityType, optionKey, floorplans, prefs, getNextCommunityTypeOrder, getNextFinancialOrder));
        }
    }
    communityTypePrompts.sort((a, b) => a.order - b.order);
    settingsJson.push(communityQuestion);
    settingsJson.push(...mergeJson(prefs.states));
    return settingsJson.sort((a, b) => a.order - b.order);
};
//# sourceMappingURL=settings-generator.js.map